import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-red.jpg?format=webp&quality=72&w=1400&imagetools";
import heroVideo from "@/assets/aviation/private-jet-hero.mp4";
import midImage from "@/assets/sprinter-exec-yellow-cabin.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/fbo-transportation-atlanta";
const PAGE_TITLE = "FBO Transportation Atlanta | Private Jet & VIP Chauffeur Service";
const PAGE_DESC =
  "Discreet FBO transportation in Atlanta for private jet, celebrity, and executive clients. NDA-bound Sprinter and SUV chauffeurs stage planeside at Signature, Atlantic Aviation, Million Air, Epps, and every PDK, FTY, RYY & ATL private aviation terminal.";

const faqs = [
  {
    q: "Which Atlanta FBOs do you service?",
    a: "We provide chauffeur transportation at every major Atlanta-area FBO including Signature Flight Support, Atlantic Aviation, Million Air, Epps Aviation at PDK, Fulton Jet Center and Hill Aircraft at FTY, and the Atlantic Aviation FBO at RYY.",
  },
  {
    q: "Do you coordinate directly with my flight department?",
    a: "Yes. Our private aviation desk works directly with flight departments, schedulers, and concierge teams to confirm tail numbers, FBO assignments, passenger manifests, and any last-minute schedule changes.",
  },
  {
    q: "Can the chauffeur meet us planeside on the tarmac?",
    a: "Absolutely. Where the FBO permits ramp access, your Mercedes-Benz Sprinter or SUV is staged planeside so passengers step from the aircraft directly into the vehicle.",
  },
  {
    q: "Is FBO transportation available 24/7?",
    a: "Yes. Private aviation rarely keeps business hours, and neither do we. Our FBO transportation desk sends chauffeurs around the clock, every day of the year.",
  },
  {
    q: "Do you provide confidential transportation for celebrities and high-profile clients?",
    a: "Yes. Chauffeurs assigned to entertainment and executive-protection work sign confidentiality agreements, vehicles are unbranded with privacy glass, and planeside staging keeps principals out of the FBO lobby entirely. We coordinate directly with security details, tour managers, and studio transportation coordinators.",
  },
  {
    q: "Can you serve FBOs outside metro Atlanta?",
    a: "We reposition statewide. Beyond PDK, FTY, RYY, LZU, and the ATL private terminals, our chauffeurs cover Savannah/Hilton Head, Augusta Regional and Daniel Field, Athens Ben Epps, Macon, Columbus, Brunswick Golden Isles, and other Georgia fields on request.",
  },
  {
    q: "What information does the FBO desk need to confirm a pickup?",
    a: "Tail number, FBO name, estimated wheels-down time, passenger count, and luggage volume. Everything else, tracking, staging, ramp authorization, and chauffeur assignment, is handled on our side.",
  },
  {
    q: "What happens if the aircraft diverts to another airport?",
    a: "Our team monitors the tail number in real time. If weather sends you to a different Georgia field, we reposition the vehicle to the new FBO rather than cancel the reservation.",
  },
  {
    q: "Do you transport flight crews separately from passengers?",
    a: "Yes. Crew hotel transfers, second-vehicle baggage runs, and separate principal and staff vehicles can all be attached to the same reservation.",
  },
];

const routeTable = {
  title: "Atlanta FBO Transfer Distances & Drive Times",
  intro:
    "Realistic drive times from Atlanta's private aviation terminals to the destinations our aviation clients request most.",
  rows: [
    {
      route: "Atlantic Aviation PDK → Buckhead",
      distance: "9 miles",
      time: "18–30 min",
      notes: "Clairmont Rd to GA-400; the shortest private arrival into Buckhead.",
    },
    {
      route: "Signature PDK → Midtown / Fox Theatre",
      distance: "13 miles",
      time: "22–38 min",
      notes: "Event nights require early staging near the venue loading area.",
    },
    {
      route: "Fulton Jet Center FTY → Mercedes-Benz Stadium",
      distance: "9 miles",
      time: "15–28 min",
      notes: "Closest private field to the stadium and State Farm Arena.",
    },
    {
      route: "Signature ATL → Downtown Atlanta",
      distance: "12 miles",
      time: "20–35 min",
      notes: "Private terminal access off Loop Rd, away from commercial curbside.",
    },
    {
      route: "Atlantic Aviation RYY → The Battery / Truist Park",
      distance: "12 miles",
      time: "18–28 min",
      notes: "Best north-metro option; skips the ATL and I-285 congestion entirely.",
    },
    {
      route: "Any Atlanta FBO → Lake Oconee / Reynolds",
      distance: "85 miles",
      time: "1 hr 30 min – 2 hr",
      notes: "Common resort leg; Sprinter recommended for luggage and golf bags.",
    },
  ],
  footnote:
    "Ranges reflect typical traffic for a chauffeured Sprinter. Our team pads every itinerary against live conditions and actual wheels-down time.",
};

const detailSections = [
  {
    title: "Planeside Protocol at Atlanta's Private Aviation Terminals",
    body: [
      "Each Atlanta FBO handles ramp access differently, and knowing those differences is what separates an aviation chauffeur from a car service. Atlantic Aviation and Signature at PDK authorize vetted operators onto the ramp for planeside staging; Million Air and Epps Aviation typically load at the private canopy steps from the lounge; Fulton Jet Center and Hill Aircraft at FTY stage curbside within the secured private gate.",
      "Wherever the facility permits, your Mercedes-Benz Sprinter or Suburban SUV is positioned before the airstairs deploy, luggage moves straight from the cargo hold to the vehicle, and passengers never pass through a public terminal.",
    ],
    list: [
      "Signature Flight Support (ATL), private terminal, secured gate loading",
      "Atlantic Aviation (PDK), planeside staging where authorized",
      "Signature Flight Support (PDK), planeside or canopy loading",
      "Million Air (PDK), lounge-to-canopy meet",
      "Epps Aviation (PDK), canopy loading with crew coordination",
      "Fulton Jet Center & Hill Aircraft (FTY), secured private-gate curbside",
      "Atlantic Aviation (RYY), planeside for north-metro arrivals",
    ],
  },
  {
    title: "Celebrity, Athlete & Executive Privacy Standards",
    body: [
      "Atlanta's film, music, and sports calendar means a large share of our FBO work involves recognizable passengers. The operating standard is simple: nothing about the movement should be visible or shareable.",
      "Chauffeurs sign confidentiality agreements before receiving an itinerary. Vehicles carry no exterior branding and use privacy glass. Manifests stay internal to assigned staff. Where a security detail is present, we brief with them in advance and drive to their routing, not ours.",
    ],
    list: [
      "NDA-bound chauffeurs with entertainment and executive-protection experience",
      "Unmarked Sprinters and SUVs with privacy glass",
      "Planeside loading that avoids lobbies and public sightlines",
      "Convoy operation for principal, security, and crew vehicles",
      "Pre-driven studio, arena, and hotel back-of-house access routes",
      "As-directed hourly hold for shifting production schedules",
    ],
  },
  {
    title: "Working With Flight Departments & Schedulers",
    body: [
      "Schedulers get one contact and one confirmation. We accept tail numbers rather than flight numbers, monitor arrival in real time, and adjust chauffeur stage times without needing a call from the crew. Recurring trip sheets, standing vehicle preferences, and consolidated billing are available for flight departments and family offices that move through Atlanta regularly.",
    ],
  },
];

export const Route = createFileRoute("/fbo-transportation-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "FBO transportation Atlanta, FBO chauffeur Atlanta, private aviation terminal car service Atlanta, celebrity car service Atlanta airport, VIP chauffeur private jet Atlanta, Signature Flight Support Atlanta car service, Atlantic Aviation Atlanta chauffeur, Million Air PDK car service, Epps Aviation ground transportation, Georgia FBO chauffeur service",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, FBO Transportation Atlanta",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "FBO Chauffeur Transportation",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
              { type: "Airport", name: "Fulton County Airport (FTY)" },
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
              { type: "Airport", name: "Cobb County International Airport (RYY)" },
            ],
          })
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      showCorporateForm
      eyebrow="Atlanta • FBO Chauffeur Coordination"
      h1="FBO Transportation in Atlanta"
      intro="Your ground plans should fit your flight. My chauffeurs coordinate directly with your flight department for Suburban SUV and Mercedes-Benz Sprinter Limousine transfers at Atlanta-area FBOs."
      heroImage={heroImage}
      heroVideo={heroVideo}
      heroAlt="Executive Mercedes-Benz Sprinter interior with captain's chairs for Atlanta FBO transfers"
      midImage={midImage}
      midAlt="Executive Sprinter cabin with ambient lighting for Atlanta FBO passengers"
      sectionOneTitle="Your Flight Crew Has Our Details"
      sectionOneBody={[
        "We serve Signature Flight Support, Atlantic Aviation, Million Air, Epps Aviation, Fulton Jet Center and Hill Aircraft across metro Atlanta.",
        "My chauffeurs coordinate with flight departments, schedulers and concierge teams for executives, family offices, principals, athletes and entertainment clients. Planeside pickup depends on FBO authorization.",
      ]}
      bulletsTitle="What Sets Our FBO Service Apart"
      bulletsIntro="Your tail number, meeting point and onward itinerary coordinated together."
      bullets={[
        "Direct flight department & scheduler coordination",
        "Live tail-number tracking with FBO arrival monitoring",
        "Planeside ramp access where authorized",
        "Discreet, formally-attired chauffeurs trained in private aviation protocol",
        "Suburban SUVs for up to 6 or 7; Mercedes-Benz Sprinter Limousines for groups",
        "Multi-leg, multi-passenger, and crew transportation",
      ]}
      coverageTitle="Every Major Atlanta FBO Covered"
      coverageIntro="Our chauffeurs operate daily at the FBOs serving Atlanta's busiest private aviation airports, PDK, FTY, RYY, and the ATL private terminals."
      coverage={[
        "Signature Flight Support, ATL",
        "Atlantic Aviation, PDK",
        "Million Air, PDK",
        "Signature Flight Support, PDK",
        "Epps Aviation, PDK",
        "Fulton Jet Center, FTY",
        "Hill Aircraft, FTY",
        "Atlantic Aviation, RYY",
        "Briscoe Field FBO, LZU",
      ]}
      routeTable={routeTable}
      detailSections={detailSections}
      faqs={faqs}
      related={[
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "Our flagship private aviation chauffeur page.",
        },
        {
          to: "/car-service-pdk-airport",
          title: "Car Service PDK Airport",
          desc: "Dedicated chauffeur service for DeKalb-Peachtree.",
        },
        {
          to: "/private-jet-airports-georgia",
          title: "Georgia Private Jet Airports",
          desc: "Statewide FBO and private terminal chauffeur coverage.",
        },
        {
          to: "/luxury-car-service-atlanta",
          title: "Luxury Car Service Atlanta",
          desc: "Discreet private car service for celebrities, executives, and private flyers.",
        },
      ]}
      ctaTitle="Text Me Your Tail Number"
      ctaBody="Send your FBO, arrival time, destination, passengers and bags. I'll check availability personally and send your quote in writing."
    />
  );
}
